import { FC, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import IconEdit from 'src/assets/images/icons/edit-2.svg';
import clsx from 'clsx';
import { format } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportIcon from '@mui/icons-material/Report';
import InfoIcon from '@mui/icons-material/Info';
import { StageEntryDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum } from 'src/utils/utility';
import { OpsAdminQuestionnaire } from './OpsAdminQuestionnaire';

interface StageEntryItemProps {
  entry: StageEntryDto;
  isLast?: boolean;
}

type StatusVariant = 'validated' | 'rejected' | 'waiting';

const ACTION_CATEGORY_MAP: Record<string, StatusVariant> = {
  VALIDATED: 'validated',
  REJECTED: 'rejected',
  WAITING: 'waiting',
};

const getVariant = (actionCategory?: string): StatusVariant => ACTION_CATEGORY_MAP[(actionCategory || '').toUpperCase()] ?? 'waiting';

const STATUS_LABEL: Record<StatusVariant, string> = {
  validated: 'Validated',
  rejected: 'Rejected',
  waiting: 'Waiting for a decision',
};

const StatusIcon: FC<{ variant: StatusVariant }> = ({ variant }) => {
  if (variant === 'validated') return <CheckCircleIcon sx={{ color: '#00992B', fontSize: 20 }} />;
  if (variant === 'rejected') return <ReportIcon sx={{ color: '#E72547', fontSize: 20 }} />;
  return <InfoIcon sx={{ color: '#bfbfbf', fontSize: 20 }} />;
};

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return null;
  try {
    return format(new Date(dateStr), 'dd-MMM-yyyy hh.mm aa zzz');
  } catch {
    return dateStr;
  }
};

const STAGE_DISPLAY_NAME: Record<string, string> = {
  OPS_ADMIN_REVIEW: 'Ops Admin',
  FINANCE_REVIEW: 'Finance',
};

const getStageDisplayName = (stage?: string) => STAGE_DISPLAY_NAME[(stage || '').toUpperCase()] || beautifyEnum(stage || '');

const ACCEPT_INVOICE_MAP: Record<string, string> = {
  VALIDATED: 'yes',
  REJECTED: 'no',
};

const STATUS_LABEL_COLOR: Record<StatusVariant, string> = {
  validated: 'text-green-50',
  rejected: 'text-red-40',
  waiting: 'text-blue-30',
};

export const StageEntryItem: FC<StageEntryItemProps> = ({ entry, isLast }) => {
  const variant = getVariant(entry.actionCategory);
  const isWaiting = variant === 'waiting';
  const isFinance = entry?.stage === 'FINANCE_REVIEW';
  const showQuestionnaire = isWaiting && isFinance;
  const [isQuestionnaireReadOnly, setIsQuestionnaireReadOnly] = useState(!isWaiting);

  return (
    <div className={clsx('relative', !isLast && 'border-b border-dashed border-neutral-20')}>
      <div className='px-16 py-14'>
        <div className='flex items-start gap-12'>
          <StatusIcon variant={variant} />

          <div className='flex-1 min-w-0'>
            <div className='flex items-start justify-between gap-8'>
              <span className='text-14 font-semibold text-neutral-90'>{entry?.stage ? getStageDisplayName(entry?.stage) : entry?.from}</span>
              {showQuestionnaire && isQuestionnaireReadOnly && (
                <IconButton onClick={() => setIsQuestionnaireReadOnly(false)} size='small' className='flex-shrink-0'>
                  <img src={IconEdit} alt='Edit' className='w-16 xl:w-20' />
                </IconButton>
              )}
            </div>

            {showQuestionnaire ? (
              <>
                <p className='text-12 text-blue_grey-30 mt-2'>Waiting a decision</p>
                <OpsAdminQuestionnaire
                  isReadOnly={isQuestionnaireReadOnly}
                  onEdit={() => setIsQuestionnaireReadOnly(true)}
                  defaultValues={{
                    acceptInvoice: ACCEPT_INVOICE_MAP[(entry.actionCategory || '').toUpperCase()] ?? '',
                    reason: entry.comment ?? '',
                  }}
                />
              </>
            ) : isWaiting ? (
              <p className='text-12 text-blue_grey-30 mt-2'>Waiting a decision</p>
            ) : (
              <>
                <p className='text-12 mt-4'>
                  <span className={clsx('font-semibold', STATUS_LABEL_COLOR[variant])}>{STATUS_LABEL[variant]}</span>
                  <span className='text-blue_grey-30'>
                    {' '}
                    - from {entry?.from || ''} to {entry?.to || ''}
                  </span>
                </p>

                {entry.completedAt && <p className='text-12 text-blue_grey-30 mt-4'>{formatDateTime(entry.completedAt)}</p>}
              </>
            )}

            {!isWaiting && entry.comment && (
              <div className='mt-20'>
                <p className='text-12 text-neutral-80'>{entry.comment}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
